/* global fetch */
import { Modal } from 'antd';
import authTarget from './events';
import { Noop } from '../constants/types';

export class HTTPError extends Error {
  public status: number;

  public responseMessage: string;

  constructor(status: number, message: string, responseMessage: string) {
    super(message);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HTTPError);
    }

    this.name = 'HTTPError';
    this.status = status;
    this.responseMessage = responseMessage;
  }
}

const defaultFetchParams: RequestInit = {
  headers: { 'Content-Type': 'application/json' },
  credentials: 'same-origin',
};

const defaultFetchParamsCSV: RequestInit = {
  headers: { 'Content-Type': 'text/csv', Accept: 'text/csv' },
  credentials: 'same-origin',
};

// TODO: stop using plainFetch and use simpleFetch because it doesn't hide the error
// clean up all of these when we get a chance to refactor
export const plainFetch = async (url: string, params = {}) => fetch(url, { ...params, ...defaultFetchParams });

// clean up all of these when we get a chance to refactor
export const plainFetchCSV = async (url: string, params = {}) => {
  const response = await fetch(url, { ...params, ...defaultFetchParamsCSV });

  if (!response.ok) {
    const responseMessage = await response.json();
    throw new HTTPError(response.status, response.statusText, responseMessage.error);
  }
  return response;
};

export const simpleFetch = async (url: string, params = {}) => {
  const result = await fetch(url, { ...params, ...defaultFetchParams });
  if (result.status === 401) authTarget.dispatch('loggedOut');
  if (!result.ok) {
    const responseMessage = await result.json();
    throw new HTTPError(result.status, result.statusText, responseMessage.error);
  }
  return result.json();
};

export const makeApiRequestAndHandleError = async (
  apiRequestCallback: () => void,
  loginErrorCallback: Noop = () => Modal.error({
    content: 'Your user is not authenticated, please try logging in again.',
    title: 'Error',
    onOk: () => { window.location.href = '/' },
  }),
  errorCallback: Noop = () => Modal.error({ title: 'Error', content: 'There was an error, please try again.' })
): Promise<void> => {
  try {
    await apiRequestCallback();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  catch (httpError: any) {
    if (httpError?.status === 401) {
      loginErrorCallback();
    } else {
      errorCallback();
    }
  }
};

export const createAssetUrl = (id: number): string => `/api/v1/attributes/${id}/asset`;
export const downloadFileFromURI = async (uri: string, downloadFileName: string, headerType?: string) => {
  let fetchResponse;
  if (headerType === 'csv') {
    fetchResponse = await plainFetchCSV(uri);
  } else {
    fetchResponse = await plainFetch(uri);
  }

  const blob = await fetchResponse.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  a.href = url;
  a.download = downloadFileName;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const fetchWrapper = async (url: string, method: 'POST' | 'PUT' | 'DELETE', data: unknown | null, params = {}) =>
  simpleFetch(url, { ...params, method, body: data && JSON.stringify(data) });

export const simplePost = async (url: string, data: unknown | null = null, params = {}) =>
  fetchWrapper(url, 'POST', data, params);
export const simplePut = async (url: string, data: unknown | null = null, params = {}) =>
  fetchWrapper(url, 'PUT', data, params);
export const simpleDelete = async (url: string, data: unknown = null, params = {}) =>
  fetchWrapper(url, 'DELETE', data, params);
